import type { DateRange } from './dateRange';
import { post } from './http';

/**
 * Client for the overlay endpoints.
 *
 * Each overlay has its own route rather than sharing a generic query surface,
 * so the request here is the overlay's own vocabulary — a product id, a list of
 * brands — and the server owns the SQL. Nothing on this side names a column,
 * a table or a filter operator.
 */
export interface OverlayScope {
  customer_name: string;
  category?: string | undefined;
  city?: string | undefined;
  sku?: string | undefined;
}

export interface OverlayResponse<T> {
  window: { start: string; end: string; as_of: string };
  rows: T[];
  response_meta: { bytes_scanned?: number };
}

/** Drops the undefined keys the scope carries, which `.strict()` would reject. */
export function scopeBody(scope: OverlayScope, range?: DateRange): Record<string, unknown> {
  const body: Record<string, unknown> = { customer_name: scope.customer_name };
  if (scope.category) body['category'] = scope.category;
  if (scope.city) body['city'] = scope.city;
  if (scope.sku) body['sku'] = scope.sku;
  if (range) body['window'] = range;
  return body;
}

export async function postOverlay<T>(
  path: string,
  body: Record<string, unknown>,
  signal?: AbortSignal,
): Promise<OverlayResponse<T>> {
  return post<OverlayResponse<T>>(path, body, signal);
}

export const num = (v: unknown): number | null => {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v === 'string' && v.trim() !== '') {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  }
  return null;
};

export const str = (v: unknown): string =>
  typeof v === 'string' ? v : typeof v === 'number' ? String(v) : '';

/** "2026-09-04" -> "04 Sep", left alone if it is not a date. */
export function dayLabel(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  // Same reason as fmtShort in lib/dateRange: the en-GB short month is "Sept"
  // for September and three letters for the rest, so axis ticks jumped width
  // once a year. This comment's own example was already wrong in the output.
  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${String(d.getUTCDate()).padStart(2, '0')} ${MONTHS[d.getUTCMonth()]}`;
}
